import { Edit2, Copy, Trash2, Calendar } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import type { ShiftTemplate } from '@/services/schedule/templateService';

interface TemplateCardProps {
  template: ShiftTemplate;
  onEdit: (template: ShiftTemplate) => void;
  onDuplicate: (template: ShiftTemplate) => void;
  onDelete: (template: ShiftTemplate) => void;
}

export const TemplateCard = ({ template, onEdit, onDuplicate, onDelete }: TemplateCardProps) => {
  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="pb-3">
        <div className="flex justify-between items-start">
          <div className="flex-1 min-w-0">
            <CardTitle className="text-lg truncate">{template.name}</CardTitle>
            {template.description && (
              <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                {template.description}
              </p>
            )}
          </div>
          <Badge variant="secondary" className="ml-2 shrink-0">
            {template.num_weeks} {template.num_weeks === 1 ? 'Week' : 'Weeks'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-1">
            <Calendar className="h-4 w-4" />
            <span>Used {template.usage_count} times</span>
          </div>
        </div>

        {template.last_used_at && (
          <p className="text-xs text-muted-foreground">
            Last used: {format(new Date(template.last_used_at), 'MMM d, yyyy')}
          </p>
        )}

        <div className="flex gap-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            className="flex-1"
            onClick={() => onEdit(template)}
          >
            <Edit2 className="h-3 w-3 mr-1" />
            Edit
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onDuplicate(template)}
          >
            <Copy className="h-3 w-3" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onDelete(template)}
            className="text-destructive hover:text-destructive"
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
